"use client";

import { useState } from "react";
import Image from "next/image";
import { TwinMark } from "@product/components/app/twin/twin-mark";
import styles from "./explorers.module.css";

const STAGES = [
  {
    label: "Read the source",
    badge: "Two separate records",
    title: "An invoice is evidence.",
    body: "Xero records an £8,400 unpaid invoice. A Slack message mentions a payment. Access lets you read them; it does not establish that they refer to the same customer or what should count as overdue.",
  },
  {
    label: "Follow the relationship",
    badge: "Northstar · linked evidence",
    title: "Now the records have a relationship.",
    body: "An entity link connects the invoice and the message to Northstar. The payment promise adds context, but does not settle the invoice or decide your overdue-balance definition.",
  },
  {
    label: "Inspect the meaning",
    badge: "Reviewed definition · v3",
    title: "A definition makes the question answerable.",
    body: "In this example, overdue means issued, unpaid invoices past their due date; drafts are excluded. The reviewed definition counts £8,400. A promise to pay remains evidence, not a received payment.",
  },
] as const;

export default function ContextExplorer() {
  const [stage, setStage] = useState(0);
  const current = STAGES[stage]!;
  return (
    <div className={styles.explorer}>
      <div className={styles.eyebrow}>
        <TwinMark /> 01 / Follow one invoice
      </div>
      <h2 className={styles.heading}>Connected. Related. Understood.</h2>
      <p className={styles.intro}>
        Follow the same evidence through three different questions.
      </p>
      <div className={styles.steps} role="group" aria-label="Evidence stages">
        {STAGES.map((item, i) => (
          <button
            key={item.label}
            className={styles.button}
            aria-pressed={stage === i}
            onClick={() => setStage(i)}
          >
            <span>0{i + 1}</span>
            {item.label}
          </button>
        ))}
      </div>
      <div className={styles.sourceGrid}>
        <div className={styles.source}>
          <div className={styles.sourceHeader}>
            <Image
              src="/concepts/source-icons/xero.svg"
              width={18}
              height={18}
              alt=""
            />
            Xero / Invoice
          </div>
          <strong>£8,400 outstanding</strong>
          <p>INV-2048 · Northstar · past due</p>
        </div>
        <div className={styles.source}>
          <div className={styles.sourceHeader}>
            <Image
              src="/concepts/source-icons/slack.png"
              width={18}
              height={18}
              alt=""
            />
            Slack / Message
          </div>
          <strong>“Payment expected Friday.”</strong>
          <p>Account team · a promise to pay</p>
        </div>
      </div>
      <div className={styles.connection}>
        <span>
          <TwinMark />
          {current.badge}
        </span>
      </div>
      <div className={styles.definition} aria-live="polite" aria-atomic="true">
        <span className={styles.eyebrow}>
          0{stage + 1} / {current.label}
        </span>
        <h3>{current.title}</h3>
        <p>{current.body}</p>
      </div>
      <div className={styles.limit}>
        Illustrative records and business rule. Connectors retain source
        evidence; tools provide task-time capabilities; reviewed Twin
        definitions establish shared meaning. Linking records alone does not
        validate that meaning.
      </div>
    </div>
  );
}
